import { useIsMobileHook } from '@teliaee/telia-common-components';
import { TranslationsContext } from '@teliaee/telia-translate';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Heading, ITabsProps, Notice, Separator, Spinner, Tabs, TabsItem } from 'telia-front-react';
import RoamingStore from '../../stores/RoamingStore';
import DangerInformation from './DangerInformation';
import EmergencyInformation from './EmergencyInformation';
import SpecialNumbers from './SpecialNumbers';

interface SpecialAndEmergencyInformationProps {
  roamingStore?: RoamingStore;
}

const SpecialAndEmergencyInformation = (props: SpecialAndEmergencyInformationProps): JSX.Element => {
  const { translate } = React.useContext(TranslationsContext);
  const { isMobile } = useIsMobileHook();
  const {
    roamingStore: {
      selectedCountry,
      berecTable: { vasNumbers, emergencyNumbers, pwsNumbers },
      isBerecTableFetching
    }
  } = props;

  if (!selectedCountry?.eea) {
    return null;
  }

  const getDefaultTab = (): string => {
    const tabInfo = {
      'special-numbers': vasNumbers?.length,
      emergency: emergencyNumbers?.length,
      'danger-notices': pwsNumbers?.length
    };

    return Object.entries(tabInfo).find(([_key, value]) => !!value)?.[0];
  };

  const getTabsProps = (): Omit<ITabsProps, 'children'> => {
    const baseProps: Omit<ITabsProps, 'children'> = {
      'aria-labelledby': 'special-numbers-info',
      defaultCurrentTab: getDefaultTab(),
      border: 'top'
    };
    if (isMobile) {
      return {
        ...baseProps,
        navigationType: 'button-navigation',
        align: 'left',
        size: 'small'
      };
    }
    return baseProps;
  };

  const renderBerecInformation = (): JSX.Element => {
    if (!vasNumbers?.length && !emergencyNumbers?.length && !pwsNumbers?.length) {
      return;
    }
    return (
      <>
        <Separator spacing="small" color="invisible" />
        <Heading level={2} id="special-numbers-info">
          {translate('roaming.specialInformation.heading')}
        </Heading>
        <Separator spacing="xsmall" color="invisible" />
        <Tabs {...getTabsProps()} key={`special-info-table-${isMobile}`}>
          {!!vasNumbers?.length && (
            <TabsItem
              id="special-numbers"
              icon="#euro-in-box"
              label={`${translate('roaming.specialInformation.vasNumbers.label')}${isMobile ? ` (${vasNumbers.length})` : ''}`}
              tagContent={vasNumbers.length}
            >
              <SpecialNumbers />
            </TabsItem>
          )}
          {!!emergencyNumbers?.length && (
            <TabsItem
              id="emergency"
              icon="#support"
              label={`${translate('roaming.specialInformation.emergencyInformation.label')}${isMobile ? ` (${emergencyNumbers.length})` : ''}`}
              tagContent={emergencyNumbers.length}
            >
              <EmergencyInformation />
            </TabsItem>
          )}
          {!!pwsNumbers?.length && (
            <TabsItem
              id="danger-notices"
              icon="#chat"
              label={`${translate('roaming.specialInformation.dangerInformation.label')}${isMobile ? ` (${pwsNumbers.length})` : ''}`}
              tagContent={pwsNumbers.length}
            >
              <DangerInformation />
            </TabsItem>
          )}
        </Tabs>
        <Separator color="invisible" spacing="xsmall" />
        <Notice type="attention">{translate('roaming.specialInformation.legalNotice')}</Notice>
      </>
    );
  };

  return (
    <>
      {isBerecTableFetching() ? (
        <>
          <Separator spacing="small" color="invisible" />
          <Spinner />
        </>
      ) : (
        renderBerecInformation()
      )}
    </>
  );
};

export default inject('roamingStore')(observer(SpecialAndEmergencyInformation));
